import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, FileText, Mic, MicOff, MonitorUp, Paperclip, PhoneOff, Send, Users } from "lucide-react";
import { getIceServers, leaveGroupCall } from "./services/api";
import apiRoutes from "./services/apiRoutes";
import { sendCallDebugLog } from "./services/callDebugLogger";

const fallbackRtcConfig = {
    iceServers: [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
    ],
    iceTransportPolicy: "all",
};

const debugGroupRTC = true;
const fileChunkSize = 16 * 1024;
const maxFileSize = 15 * 1024 * 1024;
const maxBufferedFileBytes = 1024 * 1024;

function logGroupRTC(event, payload = {}) {
    if (debugGroupRTC) {
        console.log(`[group-rtc] ${event}`, payload);
    }
    sendCallDebugLog("group-rtc", event, payload);
}

function getMediaErrorMessage(err) {
    if (["NotAllowedError", "PermissionDeniedError", "SecurityError"].includes(err?.name)) {
        return "Microphone access is blocked. Allow microphone access, then retry the group call.";
    }
    return err?.message || "Could not access microphone.";
}

function createMessageId() {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatFileSize(size) {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function createFilePacket(fileId, chunkBuffer) {
    const header = new TextEncoder().encode(`${JSON.stringify({ fileId })}\n`);
    const chunk = new Uint8Array(chunkBuffer);
    const packet = new Uint8Array(header.byteLength + chunk.byteLength);
    packet.set(header, 0);
    packet.set(chunk, header.byteLength);
    return packet.buffer;
}

function parseFilePacket(packetBuffer) {
    const packet = new Uint8Array(packetBuffer);
    const headerEnd = packet.indexOf(10);
    if (headerEnd === -1) {
        throw new Error("Invalid file packet");
    }

    const header = JSON.parse(new TextDecoder().decode(packet.slice(0, headerEnd)));
    return {
        chunk: packet.slice(headerEnd + 1),
        fileId: header.fileId,
    };
}

function waitForBufferedAmount(channel) {
    if (channel.bufferedAmount <= maxBufferedFileBytes) {
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        channel.onbufferedamountlow = () => {
            channel.onbufferedamountlow = null;
            resolve();
        };
        channel.onerror = () => reject(new Error("Data channel failed while sending file"));
    });
}

function GroupCallSession({ user, groupCall, isOpen, onClose }) {
    const [status, setStatus] = useState("Idle");
    const [error, setError] = useState("");
    const [isMuted, setIsMuted] = useState(false);
    const [isSharingScreen, setIsSharingScreen] = useState(false);
    const [isChatReady, setIsChatReady] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState("");
    const [isSendingFile, setIsSendingFile] = useState(false);
    const [remotePeers, setRemotePeers] = useState({});

    const socketRef = useRef(null);
    const fileInputRef = useRef(null);
    const localVideoRef = useRef(null);
    const localStreamRef = useRef(null);
    const screenTrackRef = useRef(null);
    const peerConnectionsRef = useRef(new Map());
    const makingOfferRef = useRef(new Set());
    const pendingIceCandidatesRef = useRef(new Map());
    const dataChannelsRef = useRef(new Map());
    const incomingFilesRef = useRef(new Map());
    const objectUrlsRef = useRef([]);
    const remoteAudioRefs = useRef(new Map());
    const remoteVideoRefs = useRef(new Map());
    const rtcConfigRef = useRef(fallbackRtcConfig);

    const roomId = groupCall?.room_id || "";
    const participants = useMemo(() => groupCall?.participants || [], [groupCall?.participants]);

    const sendSignal = useCallback((message) => {
        const socket = socketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) {
            logGroupRTC("signal-skipped-socket-not-open", { roomId, type: message.type, to: message.to });
            return;
        }
        logGroupRTC("signal-send", { roomId, type: message.type, to: message.to });
        socket.send(JSON.stringify(message));
    }, [roomId]);

    const openChannels = useCallback(() => (
        [...dataChannelsRef.current.entries()].filter(([, channel]) => channel.readyState === "open")
    ), []);

    const refreshChatReady = useCallback(() => {
        setIsChatReady(openChannels().length > 0);
    }, [openChannels]);

    const appendChatMessage = useCallback((message) => {
        setChatMessages((messages) => [
            ...messages,
            {
                id: createMessageId(),
                ...message,
            },
        ]);
    }, []);

    const updateChatMessage = useCallback((messageId, patch) => {
        setChatMessages((messages) => messages.map((message) => (
            message.id === messageId ? { ...message, ...patch } : message
        )));
    }, []);

    const handleIncomingFileChunk = useCallback((packetBuffer) => {
        const { chunk, fileId } = parseFilePacket(packetBuffer);
        const transfer = incomingFilesRef.current.get(fileId);
        if (!transfer) return;

        transfer.chunks.push(chunk);
        transfer.receivedBytes += chunk.byteLength;
        updateChatMessage(transfer.messageId, {
            file: {
                name: transfer.name,
                progress: Math.min(100, Math.round((transfer.receivedBytes / transfer.size) * 100)),
                size: transfer.size,
                status: "Receiving",
            },
        });
    }, [updateChatMessage]);

    const handleIncomingFileMessage = useCallback((payload, peerUsername) => {
        if (payload.kind === "file-start") {
            const messageId = createMessageId();
            incomingFilesRef.current.set(payload.fileId, {
                chunks: [],
                messageId,
                name: payload.name,
                receivedBytes: 0,
                size: payload.size,
                type: payload.fileType || "application/octet-stream",
            });

            setChatMessages((messages) => [
                ...messages,
                {
                    id: messageId,
                    direction: "incoming",
                    file: {
                        name: payload.name,
                        progress: 0,
                        size: payload.size,
                        status: "Receiving",
                    },
                    from: peerUsername,
                    sentAt: payload.sentAt || new Date().toISOString(),
                },
            ]);
            return;
        }

        if (payload.kind === "file-end") {
            const transfer = incomingFilesRef.current.get(payload.fileId);
            if (!transfer) return;

            const blob = new Blob(transfer.chunks, { type: transfer.type });
            const url = URL.createObjectURL(blob);
            objectUrlsRef.current.push(url);
            incomingFilesRef.current.delete(payload.fileId);
            updateChatMessage(transfer.messageId, {
                file: {
                    name: transfer.name,
                    progress: 100,
                    size: transfer.size,
                    status: "Ready",
                    url,
                },
            });
        }
    }, [updateChatMessage]);

    const setupDataChannel = useCallback((peerUsername, channel) => {
        dataChannelsRef.current.set(peerUsername, channel);
        channel.binaryType = "arraybuffer";
        channel.bufferedAmountLowThreshold = 256 * 1024;
        logGroupRTC("datachannel-setup", { peer: peerUsername, roomId });
        channel.onopen = () => {
            logGroupRTC("datachannel-open", { peer: peerUsername, roomId });
            refreshChatReady();
        };
        channel.onclose = () => {
            logGroupRTC("datachannel-close", { peer: peerUsername, roomId });
            dataChannelsRef.current.delete(peerUsername);
            refreshChatReady();
        };
        channel.onerror = () => refreshChatReady();
        channel.onmessage = (event) => {
            if (event.data instanceof ArrayBuffer) {
                handleIncomingFileChunk(event.data);
                return;
            }

            const payload = JSON.parse(event.data);
            if (payload.kind?.startsWith("file-")) {
                handleIncomingFileMessage(payload, peerUsername);
                return;
            }

            if (payload.kind === "chat") {
                appendChatMessage({
                    body: payload.body,
                    direction: "incoming",
                    from: peerUsername,
                    sentAt: payload.sentAt || new Date().toISOString(),
                });
            }
        };
    }, [appendChatMessage, handleIncomingFileChunk, handleIncomingFileMessage, refreshChatReady, roomId]);

    const loadRtcConfig = useCallback(async () => {
        try {
            const { data } = await getIceServers();
            rtcConfigRef.current = {
                iceServers: data.iceServers || fallbackRtcConfig.iceServers,
                iceTransportPolicy: data.iceTransportPolicy || "all",
            };
            logGroupRTC("ice-config-loaded", { roomId });
        } catch (err) {
            rtcConfigRef.current = fallbackRtcConfig;
            logGroupRTC("ice-config-fallback", { message: err.message, roomId });
        }
    }, [roomId]);

    const ensureLocalAudio = useCallback(async () => {
        if (localStreamRef.current) {
            return localStreamRef.current;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            localStreamRef.current = stream;
            stream.getAudioTracks().forEach((track) => {
                track.enabled = !isMuted;
            });
            setError("");
            return stream;
        } catch (err) {
            const message = getMediaErrorMessage(err);
            setError(message);
            setStatus("Microphone permission needed");
            throw new Error(message);
        }
    }, [isMuted]);

    const attachRemoteStream = useCallback((peerUsername, stream) => {
        setRemotePeers((current) => ({
            ...current,
            [peerUsername]: { stream },
        }));

        window.setTimeout(() => {
            const audio = remoteAudioRefs.current.get(peerUsername);
            if (audio) {
                audio.srcObject = stream;
                audio.play?.().catch(() => {});
            }
            const video = remoteVideoRefs.current.get(peerUsername);
            if (video) {
                video.srcObject = stream;
                video.play?.().catch(() => {});
            }
        }, 0);
    }, []);

    const createPeerConnection = useCallback(async (peerUsername) => {
        const existing = peerConnectionsRef.current.get(peerUsername);
        if (existing) {
            return existing;
        }

        const pc = new RTCPeerConnection(rtcConfigRef.current);
        peerConnectionsRef.current.set(peerUsername, pc);
        logGroupRTC("peer-create", { peer: peerUsername, roomId });

        const stream = await ensureLocalAudio();
        stream.getTracks().forEach((track) => pc.addTrack(track, stream));
        if (screenTrackRef.current) {
            pc.addTrack(screenTrackRef.current, new MediaStream([screenTrackRef.current]));
        }

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                logGroupRTC("ice-local", { candidateType: event.candidate.type, peer: peerUsername, roomId });
                sendSignal({ type: "ice-candidate", to: peerUsername, candidate: event.candidate });
            }
        };

        pc.ontrack = (event) => {
            const [streamFromPeer] = event.streams;
            logGroupRTC("track-remote", { peer: peerUsername, tracks: streamFromPeer?.getTracks().length || 0, roomId });
            if (streamFromPeer) {
                attachRemoteStream(peerUsername, streamFromPeer);
            }
            setStatus("Connected");
        };

        pc.ondatachannel = (event) => {
            logGroupRTC("datachannel-received", { peer: peerUsername, roomId });
            setupDataChannel(peerUsername, event.channel);
        };

        pc.onnegotiationneeded = async () => {
            if (makingOfferRef.current.has(peerUsername) || pc.signalingState !== "stable") {
                logGroupRTC("negotiation-skipped", { peer: peerUsername, signalingState: pc.signalingState, roomId });
                return;
            }

            try {
                makingOfferRef.current.add(peerUsername);
                await pc.setLocalDescription(await pc.createOffer());
                logGroupRTC("renegotiation-offer-created", { peer: peerUsername, roomId });
                sendSignal({ type: "offer", to: peerUsername, description: pc.localDescription });
            } catch (err) {
                logGroupRTC("negotiation-error", { message: err.message, peer: peerUsername, roomId });
                setError(err.message);
            } finally {
                makingOfferRef.current.delete(peerUsername);
            }
        };

        pc.onconnectionstatechange = () => {
            logGroupRTC("connection-state", { peer: peerUsername, state: pc.connectionState, roomId });
            if (pc.connectionState === "failed" || pc.connectionState === "disconnected") {
                setRemotePeers((current) => {
                    const next = { ...current };
                    delete next[peerUsername];
                    return next;
                });
            }
        };

        return pc;
    }, [attachRemoteStream, ensureLocalAudio, roomId, sendSignal, setupDataChannel]);

    const createOfferForPeer = useCallback(async (peerUsername) => {
        const pc = await createPeerConnection(peerUsername);
        if (!dataChannelsRef.current.has(peerUsername)) {
            setupDataChannel(peerUsername, pc.createDataChannel("group-call-chat", { ordered: true }));
        }
        if (pc.signalingState !== "stable") {
            logGroupRTC("offer-skipped-nonstable", { peer: peerUsername, signalingState: pc.signalingState, roomId });
            return;
        }

        makingOfferRef.current.add(peerUsername);
        await pc.setLocalDescription(await pc.createOffer());
        makingOfferRef.current.delete(peerUsername);
        sendSignal({ type: "offer", to: peerUsername, description: pc.localDescription });
    }, [createPeerConnection, roomId, sendSignal, setupDataChannel]);

    const handleSignal = useCallback(async (event) => {
        const message = JSON.parse(event.data);
        if (!message.from || message.from === user.username) {
            return;
        }

        logGroupRTC("signal-receive", { from: message.from, roomId, type: message.type });
        try {
            if (message.type === "peer-list") {
                for (const peerUsername of message.peers || []) {
                    await createOfferForPeer(peerUsername);
                }
                return;
            }

            if (message.type === "peer-joined") {
                await createPeerConnection(message.from);
                return;
            }

            if (message.type === "peer-left" || message.type === "leave") {
                peerConnectionsRef.current.get(message.from)?.close();
                peerConnectionsRef.current.delete(message.from);
                setRemotePeers((current) => {
                    const next = { ...current };
                    delete next[message.from];
                    return next;
                });
                return;
            }

            if (message.type === "offer") {
                const pc = await createPeerConnection(message.from);
                await pc.setRemoteDescription(message.description);
                const pendingIceCandidates = pendingIceCandidatesRef.current.get(message.from) || [];
                pendingIceCandidatesRef.current.delete(message.from);
                for (const candidate of pendingIceCandidates) {
                    await pc.addIceCandidate(candidate);
                }
                await pc.setLocalDescription(await pc.createAnswer());
                sendSignal({ type: "answer", to: message.from, description: pc.localDescription });
                return;
            }

            if (message.type === "answer") {
                const pc = peerConnectionsRef.current.get(message.from);
                if (pc && pc.signalingState === "have-local-offer") {
                    await pc.setRemoteDescription(message.description);
                    const pendingIceCandidates = pendingIceCandidatesRef.current.get(message.from) || [];
                    pendingIceCandidatesRef.current.delete(message.from);
                    for (const candidate of pendingIceCandidates) {
                        await pc.addIceCandidate(candidate);
                    }
                }
                return;
            }

            if (message.type === "ice-candidate" && message.candidate) {
                const pc = await createPeerConnection(message.from);
                if (!pc.remoteDescription) {
                    const pending = pendingIceCandidatesRef.current.get(message.from) || [];
                    pending.push(message.candidate);
                    pendingIceCandidatesRef.current.set(message.from, pending);
                    return;
                }
                await pc.addIceCandidate(message.candidate);
            }
        } catch (err) {
            logGroupRTC("signal-error", { message: err.message, roomId, type: message.type });
            setError(err.message);
        }
    }, [createOfferForPeer, createPeerConnection, roomId, sendSignal, user.username]);

    const cleanup = useCallback(() => {
        sendSignal({ type: "leave" });
        socketRef.current?.close();
        socketRef.current = null;
        dataChannelsRef.current.forEach((channel) => channel.close());
        dataChannelsRef.current.clear();
        peerConnectionsRef.current.forEach((pc) => pc.close());
        peerConnectionsRef.current.clear();
        pendingIceCandidatesRef.current.clear();
        objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        objectUrlsRef.current = [];
        incomingFilesRef.current.clear();
        screenTrackRef.current?.stop();
        screenTrackRef.current = null;
        localStreamRef.current?.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
        remoteAudioRefs.current.clear();
        remoteVideoRefs.current.clear();
        setRemotePeers({});
        setIsChatReady(false);
        setIsSendingFile(false);
        setIsSharingScreen(false);
        setStatus("Idle");
    }, [sendSignal]);

    const start = useCallback(async () => {
        if (!roomId || !user?.username) return;
        setStatus("Connecting");
        setError("");
        await loadRtcConfig();
        await ensureLocalAudio();

        const socket = new WebSocket(`${apiRoutes.CHAT_WS_URL}${apiRoutes.chat.groupCallSocket(roomId, user.username)}`);
        socketRef.current = socket;
        socket.onopen = () => {
            logGroupRTC("ws-open", { roomId, user: user.username });
            setStatus("Waiting for participants");
            sendSignal({ type: "ready" });
        };
        socket.onmessage = handleSignal;
        socket.onerror = () => setError("Group signaling connection failed");
        socket.onclose = () => logGroupRTC("ws-close", { roomId, user: user.username });
    }, [ensureLocalAudio, handleSignal, loadRtcConfig, roomId, sendSignal, user?.username]);

    useEffect(() => {
        if (isOpen) {
            start().catch((err) => setError(err.message));
        }
        return cleanup;
    }, [cleanup, isOpen, start]);

    if (!isOpen) return null;

    const leave = async () => {
        try {
            if (groupCall?.group_call_id) {
                await leaveGroupCall(groupCall.group_call_id);
            }
        } catch (err) {
            logGroupRTC("leave-api-error", { message: err.message, roomId });
        } finally {
            cleanup();
            onClose();
        }
    };

    const toggleMute = () => {
        localStreamRef.current?.getAudioTracks().forEach((track) => {
            track.enabled = isMuted;
        });
        setIsMuted((value) => !value);
    };

    const sendChatMessage = () => {
        const body = chatInput.trim();
        const channels = openChannels();
        if (!body || channels.length === 0) return;

        const sentAt = new Date().toISOString();
        channels.forEach(([, channel]) => {
            channel.send(JSON.stringify({ kind: "chat", body, sentAt }));
        });
        appendChatMessage({ body, direction: "outgoing", from: user.username, sentAt });
        setChatInput("");
    };

    const sendFile = async (file) => {
        const channels = openChannels();
        if (!file || channels.length === 0) return;
        if (file.size > maxFileSize) {
            setError(`File is too large. Limit is ${formatFileSize(maxFileSize)}.`);
            return;
        }

        const fileId = createMessageId();
        const messageId = createMessageId();
        const sentAt = new Date().toISOString();
        setIsSendingFile(true);
        setChatMessages((messages) => [
            ...messages,
            {
                id: messageId,
                direction: "outgoing",
                file: {
                    name: file.name,
                    progress: 0,
                    size: file.size,
                    status: "Sending",
                },
                from: user.username,
                sentAt,
            },
        ]);

        try {
            channels.forEach(([, channel]) => {
                channel.send(JSON.stringify({
                    fileId,
                    fileType: file.type,
                    kind: "file-start",
                    name: file.name,
                    sentAt,
                    size: file.size,
                }));
            });

            let offset = 0;
            while (offset < file.size) {
                const chunkBuffer = await file.slice(offset, offset + fileChunkSize).arrayBuffer();
                for (const [, channel] of channels) {
                    if (channel.readyState === "open") {
                        await waitForBufferedAmount(channel);
                        channel.send(createFilePacket(fileId, chunkBuffer));
                    }
                }
                offset += chunkBuffer.byteLength;
                updateChatMessage(messageId, {
                    file: {
                        name: file.name,
                        progress: Math.min(100, Math.round((offset / file.size) * 100)),
                        size: file.size,
                        status: "Sending",
                    },
                });
            }

            channels.forEach(([, channel]) => {
                if (channel.readyState === "open") {
                    channel.send(JSON.stringify({ fileId, kind: "file-end" }));
                }
            });
            updateChatMessage(messageId, {
                file: {
                    name: file.name,
                    progress: 100,
                    size: file.size,
                    status: "Sent",
                },
            });
        } catch (err) {
            setError(err.message || "Could not send file");
            updateChatMessage(messageId, {
                file: {
                    name: file.name,
                    progress: 0,
                    size: file.size,
                    status: "Failed",
                },
            });
        } finally {
            setIsSendingFile(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleFileSelected = (event) => {
        const [file] = event.target.files || [];
        if (file) {
            sendFile(file);
        }
    };

    const handleChatKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            sendChatMessage();
        }
    };

    const startScreenShare = async () => {
        if (!navigator.mediaDevices?.getDisplayMedia) {
            setError("Screen sharing is not supported in this browser.");
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
            const [track] = stream.getVideoTracks();
            screenTrackRef.current = track;
            peerConnectionsRef.current.forEach((pc) => {
                pc.addTrack(track, stream);
            });
            if (localVideoRef.current) {
                localVideoRef.current.srcObject = stream;
            }
            setIsSharingScreen(true);
            sendSignal({ type: "screen-share-started" });
            track.onended = () => stopScreenShare();
        } catch (err) {
            setError(err.message || "Could not start screen sharing");
        }
    };

    const stopScreenShare = () => {
        const track = screenTrackRef.current;
        if (!track) return;

        peerConnectionsRef.current.forEach((pc) => {
            pc.getSenders()
                .filter((sender) => sender.track === track)
                .forEach((sender) => pc.removeTrack(sender));
        });
        track.stop();
        screenTrackRef.current = null;
        setIsSharingScreen(false);
        sendSignal({ type: "screen-share-stopped" });
        if (localVideoRef.current) {
            localVideoRef.current.srcObject = null;
        }
    };

    return (
        <section className="group-call-workspace">
            <div className="group-call-panel">
                <div className="call-header">
                    <div>
                        <h2>{groupCall?.title || "Group call"}</h2>
                        <p>{status}</p>
                    </div>
                    <button className="call-close-button" onClick={leave} title="Leave group call">
                        <PhoneOff size={18} />
                    </button>
                </div>

                <div className="group-call-grid">
                    <div className="group-call-tile is-local">
                        {isSharingScreen ? (
                            <video ref={localVideoRef} autoPlay muted playsInline />
                        ) : (
                            <Users size={22} />
                        )}
                        <strong>{user.username}</strong>
                        <span>{isMuted ? "Muted" : "You"}</span>
                    </div>
                    {participants.filter((participant) => participant !== user.username).map((participant) => (
                        <div className="group-call-tile" key={participant}>
                            <Users size={22} />
                            <strong>{participant}</strong>
                            <span>{remotePeers[participant] ? "Connected" : "Waiting"}</span>
                            <video
                                ref={(element) => {
                                    if (element) remoteVideoRefs.current.set(participant, element);
                                }}
                                autoPlay
                                playsInline
                            />
                            <audio
                                ref={(element) => {
                                    if (element) remoteAudioRefs.current.set(participant, element);
                                }}
                                autoPlay
                                playsInline
                            />
                        </div>
                    ))}
                </div>

                <div className="call-chat group-call-chat">
                    <div className="call-chat-header">
                        <h3>Group chat</h3>
                        <span>{isChatReady ? "Connected" : "Waiting for peers"}</span>
                    </div>
                    <div className="call-chat-messages" aria-live="polite">
                        {chatMessages.length === 0 ? (
                            <p className="call-chat-empty">Group messages and files will appear here.</p>
                        ) : (
                            chatMessages.map((message) => (
                                <div
                                    className={`call-chat-message ${message.direction === "outgoing" ? "is-outgoing" : ""}`}
                                    key={message.id}
                                >
                                    <strong>{message.direction === "outgoing" ? "You" : message.from}</strong>
                                    {message.file ? (
                                        <span className="file-transfer">
                                            <span className="file-transfer-icon"><FileText size={16} /></span>
                                            <span className="file-transfer-copy">
                                                <span>{message.file.name}</span>
                                                <small>
                                                    {formatFileSize(message.file.size)} - {message.file.status}
                                                    {message.file.progress < 100 ? ` ${message.file.progress}%` : ""}
                                                </small>
                                                {message.file.progress < 100 && (
                                                    <span className="file-transfer-progress">
                                                        <span style={{ width: `${message.file.progress}%` }} />
                                                    </span>
                                                )}
                                            </span>
                                            {message.file.url && (
                                                <a href={message.file.url} download={message.file.name} title="Download file">
                                                    <Download size={16} />
                                                </a>
                                            )}
                                        </span>
                                    ) : (
                                        <span>{message.body}</span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                    <div className="call-chat-composer">
                        <input
                            ref={fileInputRef}
                            className="file-input"
                            type="file"
                            onChange={handleFileSelected}
                            disabled={!isChatReady || isSendingFile}
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={!isChatReady || isSendingFile}
                            title="Attach file"
                        >
                            <Paperclip size={16} />
                        </button>
                        <textarea
                            value={chatInput}
                            onChange={(event) => setChatInput(event.target.value)}
                            onKeyDown={handleChatKeyDown}
                            placeholder="Write to the group"
                            rows={2}
                            disabled={!isChatReady}
                        />
                        <button onClick={sendChatMessage} disabled={!isChatReady || !chatInput.trim()} title="Send message">
                            <Send size={16} />
                        </button>
                    </div>
                </div>

                {error && <div className="call-error-panel"><p className="error-message">{error}</p></div>}

                <div className="call-controls">
                    <button onClick={toggleMute} disabled={!localStreamRef.current}>
                        {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
                        <span>{isMuted ? "Unmute" : "Mute"}</span>
                    </button>
                    <button
                        onClick={isSharingScreen ? stopScreenShare : startScreenShare}
                        disabled={peerConnectionsRef.current.size === 0}
                    >
                        <MonitorUp size={18} />
                        <span>{isSharingScreen ? "Stop sharing" : "Share screen"}</span>
                    </button>
                    <button className="control-button is-danger" onClick={leave}>
                        <PhoneOff size={18} />
                        <span>Leave</span>
                    </button>
                </div>
            </div>
        </section>
    );
}

export default GroupCallSession;
